/**
 * Finds the case's processed documents by file name, on the server: a case can hold more documents
 * than one answer carries, and an older one must still be found by its name. Only a processed
 * document has entities to filter by. Nothing is asked until the picker opens, and closing it
 * forgets what was typed.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { DocumentOption, DocumentSearch } from '@/components/search/DocumentFilterPicker'
import { useDebounced } from '@/hooks/use-debounced'
import { api } from '@/services/api'

const RESULTS = 50

export function useDocumentSearch(caseId: string): DocumentSearch {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const term = useDebounced(query.trim(), 250)
  const search = useQuery({
    queryKey: ['case', caseId, 'documents', 'research-filter', term] as const,
    queryFn: async (): Promise<DocumentOption[]> => {
      const documents = await api.cases.getCaseDocuments(caseId, {
        limit: RESULTS,
        status: ['processed'],
        search: term,
      })
      return documents.map(document => ({
        id: document.id,
        name: document.filename,
        fileType: document.file_type,
      }))
    },
    enabled: open && Boolean(caseId),
    placeholderData: keepPreviousData,
  })
  const status: DocumentSearch['status'] = search.isError
    ? 'failed'
    : search.isSuccess && !search.isPlaceholderData && term === query.trim()
      ? 'ready'
      : 'searching'
  return {
    open,
    onOpenChange: next => {
      setOpen(next)
      if (!next) setQuery('')
    },
    query,
    onQueryChange: setQuery,
    status,
    results: search.data ?? [],
  }
}
